import { AvailableLanguages } from '@repo/toolkit/language-types'
import { Option } from '@repo/toolkit/types'
import Fuse from 'fuse.js'
import { TFunction } from 'i18next'
import qs from 'qs'
import { FieldErrors, FieldValues, Path } from 'react-hook-form'
import { toast } from 'sonner'

import { LocaleKeys } from '../../../locale/translation-types'
import { GroupedDiffusionResponse } from '../../types/models/affectedStaff/affected-staff'
import { AffectEnrollmentFailure } from '../../types/models/enrollment/affect-enrollment'
import { StatusOption } from '../../types/utils'
import { isFetchRequestError } from './fetch-error'

export const flat = <T extends { children?: object }>(array: Array<T>) => {
  let result: T[] = []
  array.forEach((a: T) => {
    result.push(a)
    if (Array.isArray(a.children)) {
      result = result.concat(flat(a.children))
    }
  })
  return result
}

export function parseError(error: Error) {
  // Check if the error.message looks like a valid JSON string
  if (error.message && error.message.startsWith('{') && error.message.endsWith('}')) {
    // Try parsing the error message as JSON
    const parsedError = JSON.parse(error.message) as {
      status?: string
      message: string
    }

    console.log('Parsed error:', parsedError)

    return parsedError
  }
}

export const extractErrorMessage = (error: Error) => {
  let message: string | undefined

  try {
    // Check if the error.message looks like a valid JSON string
    if (error.message && error.message.startsWith('{') && error.message.endsWith('}')) {
      // Try parsing the error message as JSON
      const parsedError = JSON.parse(error.message) as {
        message: string | string[]
      }
      console.log('Parsed error:', parsedError)
      // If parsedError.message is an array, use the first element, otherwise use it directly
      message = Array.isArray(parsedError.message) ? parsedError.message[0] : parsedError.message
      console.log('Extracted message:', message)
    }
  } catch (e) {
    // If parsing fails, use a default message
    message = 'Une erreur est survenue'
  }

  return message
}

export const extractAllErrorMessages = (error: Error): string[] => {
  try {
    if (error.message && error.message.startsWith('{') && error.message.endsWith('}')) {
      const parsedError = JSON.parse(error.message) as {
        message: string | string[]
      }
      return Array.isArray(parsedError.message) ? parsedError.message : [parsedError.message]
    }
  } catch {
    // fall through
  }
  return ['Une erreur est survenue']
}

export function showAxiosError(error: Error) {
  if (isFetchRequestError(error)) {
    const message =
      error.kind === 'aborted'
        ? 'La requete a ete interrompue. Veuillez reessayer.'
        : 'Impossible de joindre le serveur. Verifiez votre connexion puis reessayez.'

    toast.error(message)
    return
  }

  const messages = extractAllErrorMessages(error)
  messages.forEach((msg) => toast.error(msg))
}

export function modifyUrlSegment(url: string, target: string, value: string) {
  return url.replace(target, value)
}

export const encodeNestedObject = <T extends object>(url: string, query?: T) => {
  if (!query) return url
  return `${url}?${qs.stringify(query)}`
}

interface SortObject {
  id: string
  desc: boolean
}
export type NestedObject = { [key: string]: NestedObject | 'asc' | 'desc' }
export const createSortingObject = (arr: SortObject[]): NestedObject => {
  const result: NestedObject = {}

  arr.forEach((item) => {
    const keys = item.id.split('_')
    let current: NestedObject = result

    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        // If it's the last key, assign "asc" or "desc" based on the desc property
        current[key] = item.desc ? 'desc' : 'asc'
      } else {
        // If it's not the last key, ensure the key is an object
        if (!current[key] || typeof current[key] !== 'object') current[key] = {} as NestedObject
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
        current = current[key] as any
      }
    })
  })

  return result
}

export function getSelectedOption<T>(
  value: T | undefined,
  label?: string | undefined | null
): Option<T> | undefined {
  if (!label) return undefined
  return !value ? undefined : { label, value }
}

export function getSelectedEnumOption<T extends string>(
  value: T | undefined | null,
  mapper: Record<T, string | StatusOption>,
  t?: TFunction<AvailableLanguages, undefined>
): Option<T> | undefined {
  if (!value) return undefined
  const mappedValue = mapper[value]
  if (t) {
    return {
      label:
        typeof mappedValue === 'string'
          ? t(mappedValue as LocaleKeys)
          : t(mappedValue.label as LocaleKeys),
      value
    }
  }
  return {
    label: typeof mappedValue === 'string' ? mappedValue : mappedValue.label,
    value
  }
}

export function getDeepFormError<T extends FieldValues>(
  error: FieldErrors<T>,
  deepKeys: string[]
) {
  let currentError: unknown = error
  for (const key of deepKeys) {
    if (!currentError) return undefined
    currentError = currentError[key as keyof typeof currentError]
  }
  return currentError as FieldErrors<T>[Path<T>]
}

export const checkForFrance = (country: string) => {
  const list = ['france', 'french', 'fr', 'francais', 'français']
  const fuse = new Fuse(list, {
    includeScore: true,
    threshold: 0.4
  })

  const result = fuse.search(country.toLocaleLowerCase())
  return result.length > 0 && result[0].score !== undefined && result[0].score < 0.4
}

export const ssnPattern = (ssn: string) => {
  return ssn
    .replace(/([\dAB]{1})(?=[\dAB]{2})/, '$1 ') // Space after first digit
    .replace(/([\dAB]{2})(?=[\dAB]{2})/, '$1 ') // Space after first 2 digits
    .replace(/([\dAB]{2})(?=[\dAB]{2})/, '$1 ') // Space after second 2 digits
    .replace(/([\dAB]{2})(?=[\dAB]{3})/, '$1 ') // Space after first 3 digits
    .replace(/([\dAB]{3})(?=[\dAB]{3})/, '$1 ') // Space after second 3 digits
    .replace(/([\dAB]{2})(?=[\dAB]{2}$)/, '$1 ') // Space after the last 2 digits
    .trim()
}

//this function format ssn as follows: if i have  201129769760166, it will return 2 01 12 97 697 601 66
export const formatSsn = (ssn: string) => {
  // Remove non-digit characters, keeping A/B for Corsica departments (2A, 2B)
  const cleanedSsn = ssn.toUpperCase().replace(/[^\dAB]/g, '')

  // Format the SSN progressively, inserting spaces at the correct places
  const formattedSsn = ssnPattern(cleanedSsn)

  return formattedSsn
}

/** A staff diffusion result: a success only when everyone received it, otherwise a warning naming who did not and why */
export const toastDiffusionResult = ({ message, partial }: GroupedDiffusionResponse) => {
  if (partial) toast.warning(message, { duration: 10000 })
  else toast.success(message)
}

// The per-enrollment refusals the affectation endpoint sends back when it placed nobody
export const extractAffectEnrollmentFailures = (error: Error): AffectEnrollmentFailure[] => {
  try {
    if (error.message && error.message.startsWith('{') && error.message.endsWith('}')) {
      const parsedError = JSON.parse(error.message) as { failures?: AffectEnrollmentFailure[] }
      return parsedError.failures ?? []
    }
  } catch {
    // fall through
  }
  return []
}
